import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Car, IdentificationCard } from "@phosphor-icons/react";
import CalendarView from "@/components/CalendarView";
import CalendarEventDetail from "@/components/CalendarEventDetail";

// Admin view of any driver's calendar. Read-only: admin cannot edit personal
// events or unavailability, but can switch between drivers and inspect each
// item. A fast driver-picker dropdown sits at the top.
export default function AdminDriverCalendar() {
  const { driverId } = useParams();
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/drivers").then((r) => {
      const approved = (r.data || []).filter((d) => d.account_status !== "pending");
      setDrivers(approved);
      const found = approved.find((d) => d.id === driverId) || approved[0] || null;
      setSelected(found);
    }).catch(() => {
      // Phase N1A — do NOT clear `drivers` on fetch failure: that would
      // make the picker show "Aucun chauffeur" indistinguishably from a
      // real empty state. Keep the previous list (possibly stale) and
      // let the next mount retry.
    });
  }, [driverId]);

  const changeDriver = useCallback((id) => {
    const d = drivers.find((x) => x.id === id);
    setSelected(d || null);
    navigate(`/admin/drivers/${id}/calendar`, { replace: true });
  }, [drivers, navigate]);

  return (
    <div data-testid="admin-driver-calendar" className="min-h-screen bg-[#050505] text-white">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-[#050505]/80 border-b border-zinc-800/50">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center gap-2">
          <Button data-testid="back-btn" variant="ghost" size="sm" onClick={() => navigate("/admin")}
            className="text-zinc-400 hover:text-white hover:bg-zinc-800 px-2"><ArrowLeft size={18} /></Button>
          <h1 className="text-base sm:text-lg font-medium truncate">Calendrier chauffeur</h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-3 sm:px-4 py-4 space-y-4">
        <div className="bg-[#121212] border border-zinc-800 rounded-lg p-3 sm:p-4 space-y-3">
          <div className="flex items-center gap-2">
            <IdentificationCard size={16} className="text-zinc-400" />
            <select
              data-testid="driver-picker"
              value={selected?.id || ""}
              onChange={(e) => changeDriver(e.target.value)}
              className="flex-1 bg-[#1A1A1A] border border-zinc-700 text-white rounded h-10 px-3 text-sm focus:border-amber-500 focus:outline-none"
            >
              {drivers.length === 0 && <option value="">Aucun chauffeur</option>}
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name || d.email} {d.vehicle_model ? `— ${d.vehicle_model}` : ""}
                </option>
              ))}
            </select>
          </div>
          {selected && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-400">
              {selected.phone && <span>Tél : <span className="text-zinc-200">{selected.phone}</span></span>}
              {selected.vehicle_model && <span className="inline-flex items-center gap-1"><Car size={12} />{selected.vehicle_model} {selected.vehicle_year}</span>}
              {selected.plate_number && <span>Plaque : <span className="text-zinc-200">{selected.plate_number}</span></span>}
            </div>
          )}
        </div>

        {selected ? (
          <CalendarView
            driverId={selected.id}
            readOnly
            onEventClick={(ev) => setDetail(ev)}
          />
        ) : (
          <p className="text-sm text-zinc-500">Sélectionnez un chauffeur.</p>
        )}
      </main>

      <CalendarEventDetail event={detail} onClose={() => setDetail(null)} readOnly />
    </div>
  );
}
